import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';

import { FormBuilder } from '@angular/forms';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NotificationService } from 'app/_services/notification.service';

import { BuyerService } from '../_services/buyer.service';
import { BuyerCreateQuotationService } from './create-quotation.service';
import { BuyerCreateQuotationVehicleComponent } from './_components/quotation-vehicle/quotation-vehicle.component';
import { BuyerCreateQuotationPartsComponent } from './_components/quotation-parts/quotation-parts.component';
import { BuyerCreateQuotationObjectiveComponent } from './_components/quotation-objective/quotation-objective.component';
import { BuyerCreateQuotationOtherInfosComponent } from './_components/quotation-other-infos/quotation-other-infos.component';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { quotationPartFactory } from './_models/quotationPart';
import { UploadQuotationFileResponseModel } from './_models/uploadQuotationFileResponseModel';
import { BuyerCreateQuotationImportBudgetComponent } from './_components/quotation-import-budget/quotation-import-budget.component';
import { getQuotationPartTypeEnumFromBitwise } from 'app/_models/_enums/quotationPartType.enum';
import { quotationFactory } from './_models/quotation';
import { QuotationFile, quotationFileFactory } from './_models/quotationFile';
import { AccountService } from 'app/account/account.service';
import { UserService } from 'app/_services/user.service';
import { MixPanelService } from 'app/_services/mix-panel.service';
import { MixPanelEventEnum } from 'app/_models/mixPanelEventEnum';
import { Router } from '@angular/router';
import { Globals } from 'app/_models/globals';

declare const isEmpty: any;

@Component({
    selector: 'app-create-quotation', templateUrl: './create-quotation.component.html', styleUrls: ['./create-quotation.component.scss'],
    standalone: false
})
export class BuyerCreateQuotationComponent implements OnInit {
    @ViewChild(BuyerCreateQuotationVehicleComponent) quotationVehicleComponent: BuyerCreateQuotationVehicleComponent;
    @ViewChild(BuyerCreateQuotationPartsComponent) quotationPartsComponent: BuyerCreateQuotationPartsComponent;
    @ViewChild(BuyerCreateQuotationObjectiveComponent) quotationObjectiveComponent: BuyerCreateQuotationObjectiveComponent;
    @ViewChild(BuyerCreateQuotationOtherInfosComponent) quotationOtherInfosComponent: BuyerCreateQuotationOtherInfosComponent;
    @ViewChild(BuyerCreateQuotationImportBudgetComponent) public quotationImportBudgetComponent: BuyerCreateQuotationImportBudgetComponent;

    public partsQuantity: number = 0;
    public createQuotationIsDisabled: boolean = true;
    public isQuotationWithParts: boolean = true;
    public inputOrcamento: QuotationFile = null;
    public inputFiles: any;
    public userERP?: string = null;
    public alreadyLoadedERP: boolean = false;

    fb = inject(FormBuilder)

    constructor(
        private buyerCreateQuotationService: BuyerCreateQuotationService,
        private accountService: AccountService,
        private userService: UserService,
        private mixPanelService: MixPanelService,
        private buyerService: BuyerService,
        private notificationService: NotificationService,
        private globals: Globals,
        private location: Location,
        private ngxLoader: NgxUiLoaderService,
        private router: Router) { }

    ngOnInit(): void {
        this.getUserERPName();
    }

    clearQuotation() {
        this.inputFiles = [];
        this.inputOrcamento = null;

        this.quotationVehicleComponent.vehicleFormGroup.reset();
        this.quotationObjectiveComponent.objectiveFormGroup.setValue({
            isPurchase: true
        })

        this.quotationOtherInfosComponent.reset()
        this.quotationPartsComponent.parts = [];

        this.handleFooterUpdate({ partsCount: 0 });
    }

    async createQuotation() {
        if (this.userERP != null && !(await this.notificationService.showConfirmationDialog(this.globals.translate('buyer.create-quotation.messages.create-quotation.erp-warning').replace('{{erp}}', `${this.userERP}`), null, false))) {
            return;
        }

        var quotationHaveVehicle: boolean = !this.quotationVehicleComponent.quotationVehicleCollapsed;

        var notes = this.quotationOtherInfosComponent.quotationComment;
        var pictures = this.quotationOtherInfosComponent.quotationImages.map((image) => { return quotationFileFactory(image.imageName, image.imageFile); });
        var isOrcamento = this.quotationObjectiveComponent.isOrcamento;
        var parts = this.quotationPartsComponent.parts;
        var vehicle = quotationHaveVehicle ? this.quotationVehicleComponent.getVehicleData() : null;

        if (quotationHaveVehicle && vehicle == null)
            return;

        this.ngxLoader.startLoader('loader-principal');

        var quotation = quotationFactory(isOrcamento, notes, parts, pictures, this.inputOrcamento, vehicle);

        this.buyerCreateQuotationService.createQuotation(quotation, this.isQuotationWithParts).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                if (response.success) {
                    this.notificationService.showSuccessToastr('buyer.create-quotation.messages.create-quotation.success', { translateMessage: true });

                    let user = this.userService.user();
                    this.mixPanelService.updateUserPropertiesMixPanel(user).subscribe({ error: (e: any) => console.error(e) });
                    this.mixPanelService.trackMixPanelEvent(MixPanelEventEnum.Bid).subscribe({ error: (e: any) => console.error(e) });
                }

                this.router.navigate(['/comprador/visao-geral']);
                this.ngxLoader.stopLoader('loader-principal');
            },
            error: error => {
                this.notificationService.showErrorToastr('buyer.create-quotation.messages.create-quotation.error', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
                console.log(error);
            }
        });
    }

    getUserERPName() {
        this.alreadyLoadedERP = false;

        this.accountService.getUserERPName().subscribe({
            next: (response: ApiResponseModel<string | null>) => {
                this.userERP = response.result;
                this.alreadyLoadedERP = true;
            },
            error: error => {
                console.log(error);
                this.alreadyLoadedERP = true;
            }
        });
    }

    async onQuotationFileUpload(file: File) {
        this.inputFiles = [];

        this.ngxLoader.startLoader('loader-principal');
        var b64file = (await this.toBase64(file)).toString();
        b64file = b64file.indexOf(',') > -1 ? b64file.split(',')[1] : b64file;

        this.clearQuotation();
        this.inputOrcamento = quotationFileFactory(file.name, b64file);

        this.buyerCreateQuotationService.uploadQuotationFile(b64file).subscribe({
            next: (response: ApiResponseModel<UploadQuotationFileResponseModel>) => {
                let totalPhotos = 0;

                if (!response.success) {
                    this.notificationService.showErrorToastr('buyer.create-quotation.quotation-import-file.messages.upload.error', { translateMessage: true });
                    this.ngxLoader.stopLoader('loader-principal');
                    return;
                }

                totalPhotos = response.result.picturesUrl.length;
                const photos = response.result.picturesUrl.slice(0, 10);

                if (totalPhotos > 10)
                    this.notificationService.showWarningToastr('buyer.create-quotation.quotation-import-file.messages.upload.too-many-images', { translateMessage: true, messageParameters: { maxUploadImages: this.buyerService.maxUploadImages } });

                if (photos.length > 0) {
                    this.quotationOtherInfosComponent.loadQuotationFileImages(photos);
                }

                if (response.result.vehicle.brandId == 0)
                    response.result.vehicle.brandId = -1;

                this.quotationVehicleComponent.vehicleFormGroup.setValue(response.result.vehicle)
                this.quotationVehicleComponent.plateLoaded = true;
                this.quotationVehicleComponent.vehicleFormGroup.controls.licensePlate.disable();

                this.quotationObjectiveComponent.objectiveFormGroup.setValue({
                    isPurchase: true
                })

                this.quotationOtherInfosComponent.otherInfosFormGroup.setValue({
                    comment: response.result.notes
                });

                if (!isEmpty(response.result.items)) {
                    response.result.items.forEach(item => {
                        const partTypes = getQuotationPartTypeEnumFromBitwise(item.partType);
                        const part = quotationPartFactory(item.name, item.quantity, item.code, partTypes, false, item.insurancePrice, item.insuranceDiscount)//item.type, item.condition)

                        this.quotationPartsComponent.parts.push(part);
                    })

                    this.handleFooterUpdate({ partsCount: this.quotationPartsComponent.parts.length });
                    this.quotationImportBudgetComponent.close();
                    this.ngxLoader.stopLoader('loader-principal');
                }
            },
            error: error => {
                let firstKey = Object.keys(error.errors)[0];
                let message = firstKey && error.errors[firstKey]?.[0]?.message;

                if (message) {
                    this.notificationService.showErrorToastr(message);
                } else {
                    this.notificationService.showErrorToastr('buyer.create-quotation.quotation-import-file.messages.upload.error', { translateMessage: true });
                }

                console.log(error);
                this.quotationImportBudgetComponent.close();
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    onQuotationTypeChange($event: { isQuotationWithParts: boolean }): void {
        this.isQuotationWithParts = $event.isQuotationWithParts;
        this.quotationVehicleComponent.updateVehicleCollapse(this.isQuotationWithParts);
        this.quotationPartsComponent.updateQuotationParts(this.isQuotationWithParts);
        this.quotationOtherInfosComponent.reset();
    }

    handleFooterUpdate($event: { partsCount: number }) {
        this.partsQuantity = $event.partsCount;
        this.createQuotationIsDisabled = this.partsQuantity <= 0;
    }

    voltar() {
        this.location.back();
    }

    private toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

